import { Timer } from '@visiba/std';
import { Reporter } from './_reporter';

export interface ReconnectionPolicyContext {
	readonly reconnectInterval: number;
	readonly reconnectAscent: number;
	readonly reconnectCap: number;
	readonly reconnectMaxAttempts: number;
}

export class ReconnectionPolicy {
	private static NOOP: Disposer = () => void 0;

	private readonly reconnectTimer = new Timer();
	private reconnectAttempts = 0;
	private isAlive = true;

	constructor(
		private readonly ctx: ReconnectionPolicyContext,
		private readonly reporter: Reporter,
	) {
		// Empty
	}

	public invoke(retryMethod: () => Promise<void>, gracefulShutdown: () => void): Disposer {
		if (!this.isAlive) return ReconnectionPolicy.NOOP;

		const { reconnectAscent, reconnectCap, reconnectInterval, reconnectMaxAttempts } = this.ctx;
		const timeout = reconnectInterval * reconnectAscent ** this.reconnectAttempts++;

		this.reconnectTimer.flush();

		if (this.reconnectAttempts > reconnectMaxAttempts) {
			gracefulShutdown();
		}

		this.reconnectTimer.wait(
			async () => {
				try {
					this.reporter.log(`Tries to reconnect ...`);
					await retryMethod();

					this.reconnectTimer.flush();
					// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
				} catch (error: unknown) {
					if (!this.isAlive) return;

					this.reporter.log(`Failed to reconnect, retrying in ${timeout}ms`);

					this.invoke(retryMethod, gracefulShutdown);
				}
			},
			timeout > reconnectCap ? reconnectCap : timeout,
		);

		return () => this.dispose();
	}

	public dispose(): void {
		this.isAlive = false;
		this.reconnectTimer.flush();
	}
}

type Disposer = () => void;
